import { ref } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import axios from "../lib/axios";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole, renewToken } from "./auth";
import { LaunchError } from "../utils/errors";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";

const postAutomationFunction = async (dispatch, functionName, data) => {
  const token = await dispatch(renewToken());
  const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
  const response = await axios({
    method: "POST",
    url: `${getCloudFunctionUrl(functionName)}/${functionName}`,
    data: {
      ...data,
      advertiserID,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const startListenerPublishingAutomations =
  ({
    onAutomationsChange = () => {},
    onRuntimeChange = () => {},
    onReferencesAvailable = () => {},
  }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID) {
        onAutomationsChange({});
        onRuntimeChange({});
        return false;
      }

      const automationsRef = ref(db, `publishingAutomations/${advertiserID}`);
      const runtimeRef = ref(db, `publishingAutomationRuntime/${advertiserID}`);

      const unsubscribeAutomations = onValue(automationsRef, (snapshot) => {
        onAutomationsChange(snapshot.exists() ? snapshot.val() : {});
      });
      const unsubscribeRuntime = onValue(runtimeRef, (snapshot) => {
        onRuntimeChange(snapshot.exists() ? snapshot.val() : {});
      });
      onReferencesAvailable({
        unsubscribeAutomations,
        unsubscribeRuntime,
      });
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startListenerPublishingAutomations",
        })
      );
      return false;
    }
  };

export const stopPublishingAutomationListeners = (references = {}) => {
  if (typeof references.unsubscribeAutomations === "function") {
    references.unsubscribeAutomations();
  }
  if (typeof references.unsubscribeRuntime === "function") {
    references.unsubscribeRuntime();
  }
};

export const startCreatePublishingAutomation =
  (payload) => async (dispatch) => {
    try {
      return await postAutomationFunction(
        dispatch,
        "createPublishingAutomation",
        payload
      );
    } catch (error) {
      const body = error?.response?.data;
      if (body && typeof body === "object") {
        return body;
      }
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startCreatePublishingAutomation",
        })
      );
      return { ok: false };
    }
  };

export const startUpdatePublishingAutomation =
  (payload) => async (dispatch) => {
    try {
      return await postAutomationFunction(
        dispatch,
        "updatePublishingAutomation",
        payload
      );
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startUpdatePublishingAutomation",
        })
      );
      return { ok: false };
    }
  };

export const startPausePublishingAutomation =
  ({ automationID, optimisticSwitch }) =>
  async (dispatch) => {
    try {
      return await postAutomationFunction(
        dispatch,
        "pausePublishingAutomation",
        { automationID, optimisticSwitch }
      );
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startPausePublishingAutomation",
        })
      );
      return { ok: false, error, operationId: optimisticSwitch?.operationId };
    }
  };

export const startResumePublishingAutomation =
  ({ automationID, optimisticSwitch }) =>
  async (dispatch) => {
    try {
      return await postAutomationFunction(
        dispatch,
        "resumePublishingAutomation",
        { automationID, optimisticSwitch }
      );
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startResumePublishingAutomation",
        })
      );
      return { ok: false, error, operationId: optimisticSwitch?.operationId };
    }
  };

export const startTurnOffPublishingAutomation =
  ({ automationID }) =>
  async (dispatch) => {
    try {
      return await postAutomationFunction(
        dispatch,
        "turnOffPublishingAutomation",
        { automationID }
      );
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startTurnOffPublishingAutomation",
        })
      );
      return { ok: false };
    }
  };

export const startRenamePublishingAutomation =
  ({ automationID, name }) =>
  async (dispatch) => {
    try {
      return await postAutomationFunction(
        dispatch,
        "renamePublishingAutomation",
        { automationID, name }
      );
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startRenamePublishingAutomation",
        })
      );
      return { ok: false };
    }
  };
